import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Building2, Home as HomeIcon, Briefcase, CheckCircle2, Phone, Mail, MapPin,
  Calendar, MessageSquare, ChevronRight, Shield, Star, CreditCard, Users,
  ClipboardList, Award, Search, FileCheck, FileText, type LucideIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { addLead, TipoImovel, ROUTE_PATHS } from '@/lib/index';

const schema = z.object({
  nome: z.string().min(2, 'Nome obrigatório'),
  email: z.string().email('Email inválido'),
  telefone: z.string().min(9, 'Telefone inválido'),
  morada: z.string().min(5, 'Morada obrigatória'),
  localidade: z.string().min(2, 'Localidade obrigatória'),
  codigoPostal: z.string().regex(/^\d{4}-\d{3}$/, 'Formato: 0000-000'),
  tipoImovel: z.enum(['predio', 'moradia', 'escritorio'] as const),
  dataVisita: z.string().min(1, 'Selecione uma data'),
  notas: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const tipoOptions: { value: TipoImovel; label: string; icon: LucideIcon; desc: string }[] = [
  { value: 'predio', label: 'Prédio', icon: Building2, desc: 'Apartamento / Condomínio' },
  { value: 'moradia', label: 'Moradia', icon: HomeIcon, desc: 'Casa unifamiliar' },
  { value: 'escritorio', label: 'Escritório', icon: Briefcase, desc: 'Espaço comercial / Empresa' },
];

const trustItems: { icon: LucideIcon; title: string; desc: string }[] = [
  { icon: Star, title: '66 Anos de Experiência', desc: 'Décadas de inovação e excelência no setor de videoporteiros e segurança.' },
  { icon: Shield, title: 'Garantia de 3 Anos', desc: 'Todos os trabalhos realizados têm garantia oficial de 3 anos.' },
  { icon: Users, title: 'Rede de Instaladores', desc: 'Profissionais Comelit certificados e de confiança em todo o território nacional.' },
  { icon: CreditCard, title: 'Pagamento Seguro & Transparente', desc: '30% de sinal à adjudicação, 70% no final. A AT CONTERA gere e garante todo o processo sem custos extra.' },
];

const steps: { num: string; icon: LucideIcon; title: string; desc: string }[] = [
  { num: '01', icon: ClipboardList, title: 'Cliente faz o pedido', desc: 'Preenche o formulário com a morada, tipo de imóvel e data preferida para visita.' },
  { num: '02', icon: Search, title: 'AT CONTERA contacta instaladores', desc: 'Notificamos todos os Instaladores Certificados Comelit da zona do cliente.' },
  { num: '03', icon: FileCheck, title: 'Cliente recebe orçamentos', desc: 'A AT CONTERA contacta os instaladores certificados da zona e envia as propostas ao cliente para escolha.' },
  { num: '04', icon: Award, title: 'Garantia & Pagamento Incluídos', desc: 'Garantia de 3 anos + pagamento gerido pela AT CONTERA: 30% sinal, 70% na conclusão. Instalador pago em ≤48h.' },
];

const TIPO_LABEL: Record<string, string> = {
  predio: 'Prédio / Apartamento',
  moradia: 'Moradia',
  escritorio: 'Escritório / Espaço Comercial',
};

export default function HomePage() {
  const [submitted, setSubmitted] = useState(false);
  const [leadCode, setLeadCode] = useState('');
  const [slideIdx, setSlideIdx] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  const slides = [
    {
      img: 'https://images.unsplash.com/photo-1768096865523-ca23d8811107?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1600&q=80',
      frase: 'A Segurança não tem preço.',
      sub: 'Mas tem um especialista certificado na sua zona.',
    },
    {
      img: 'https://images.unsplash.com/photo-1758696654710-bd61aa76c8a7?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1600&q=80',
      frase: 'Há 6 décadas ao seu lado.',
      sub: '66 anos de experiência em segurança e videoporteiros.',
    },
    {
      img: 'https://images.unsplash.com/photo-1652641592410-190fb18e43f4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1600&q=80',
      frase: 'Garantia não é opção — é Obrigação.',
      sub: 'Todos os trabalhos com garantia oficial de 3 anos.',
    },
  ];

  useEffect(() => {
    const t = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setSlideIdx(i => (i + 1) % slides.length);
        setFadeIn(true);
      }, 500);
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const slide = slides[slideIdx];

  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { tipoImovel: 'moradia' },
  });

  const tipoSelecionado = watch('tipoImovel');
  const moradaWatch = watch('morada');
  const localidadeWatch = watch('localidade');
  const cpWatch = watch('codigoPostal');
  const mapsQuery = [moradaWatch, localidadeWatch, cpWatch, 'Portugal'].filter(Boolean).join(', ');
  const mapsEmbed = mapsQuery.length > 10
    ? `https://www.google.com/maps?q=${encodeURIComponent(mapsQuery)}&output=embed`
    : null;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  const onSubmit = async (data: FormData) => {
    const lead = await addLead(data as Parameters<typeof addLead>[0]);
    const code = `CTR-${lead.id.slice(0, 8).toUpperCase()}`;

    try {
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_KEY,
          subject: `Novo pedido de instalação — ${code} — ${data.localidade}`,
          from_name: data.nome,
          replyto: data.email,
          message: [
            `╔══════════════════════════════════════╗`,
            `   NOVO PEDIDO DE INSTALAÇÃO — ${code}`,
            `╚══════════════════════════════════════╝`,
            ``,
            `👤 DADOS DO CLIENTE`,
            `Nome:      ${data.nome}`,
            `Email:     ${data.email}`,
            `Telefone:  ${data.telefone}`,
            ``,
            `🏠 IMÓVEL`,
            `Tipo:          ${TIPO_LABEL[data.tipoImovel] ?? data.tipoImovel}`,
            `Morada:        ${data.morada}`,
            `Localidade:    ${data.localidade}`,
            `Código Postal: ${data.codigoPostal}`,
            ``,
            `📅 DATA PREFERIDA PARA VISITA`,
            `${data.dataVisita.split('-').reverse().join('/')}`,
            ``,
            data.notas ? `📝 NOTAS DO CLIENTE\n${data.notas}\n` : '',
            `─────────────────────────────────────────────`,
            `📋 COMO FUNCIONA O PROCESSO`,
            ``,
            `1️⃣  CLIENTE FEZ O PEDIDO — referência ${code}`,
            `2️⃣  AT CONTERA CONTACTA OS INSTALADORES CERTIFICADOS COMELIT DA ZONA`,
            `     → Todos os instaladores aprovados da zona recebem notificação.`,
            `3️⃣  AT CONTERA ENVIA AS PROPOSTAS AO CLIENTE PARA ESCOLHA`,
            `4️⃣  AT CONTERA GARANTE TODOS OS TRABALHOS COM GARANTIA DE 3 ANOS`,
            ``,
            `─────────────────────────────────────────────`,
            `💳 PAGAMENTOS — TUDO PELA AT CONTERA, SEM EXCEPÇÕES`,
            ``,
            `⚠️  NUNCA PAGUE DIRETAMENTE AO INSTALADOR.`,
            ``,
            `   → 30% de sinal pago à AT CONTERA na adjudicação`,
            `   → AT CONTERA fornece o material ao instalador`,
            `   → 70% restantes pagos à AT CONTERA na conclusão da obra`,
            `   → AT CONTERA paga ao instalador no prazo de 48 horas`,
            ``,
            `   Este serviço é 100% gratuito e transparente.`,
            `   Não existe qualquer dinheiro a circular fora da AT CONTERA.`,
            ``,
            `─────────────────────────────────────────────`,
            `🛡️  COMPROMISSO AT CONTERA`,
            ``,
            `• Todos os pedidos são realizados por Instaladores Certificados Comelit`,
            `• Garantia oficial de 3 anos em todos os trabalhos realizados`,
            `• 66 anos de experiência no setor de segurança e videoporteiros`,
            ``,
            `─────────────────────────────────────────────`,
            `Aceda ao dashboard em /dashboard para gerir este pedido.`,
          ].filter(Boolean).join('\n'),
        }),
      });

      // Email automático ao cliente com o código CTR
      if (data.email) {
        await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            access_key: import.meta.env.VITE_WEB3FORMS_KEY,
            to_email: data.email,
            subject: `AT CONTERA — O seu código de pedido: ${code}`,
            from_name: 'AT CONTERA',
            message: `Olá ${data.nome},\n\nO seu pedido foi registado com sucesso!\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n📋 O SEU CÓDIGO CTR: ${code}\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\nGuarde este código — é o seu acesso à Área do Cliente Final.\n\nCom ele pode:\n• Consultar as propostas de orçamento preparadas para si\n• Aprovar o orçamento que preferir\n• Acompanhar o estado da obra\n• Activar a garantia de 3 anos após conclusão\n\n🔗 Aceder à Área do Cliente:\n${window.location.origin}/#/garantia\n\nIntroduza o código: ${code}\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━\nDados do seu pedido:\nMorada: ${data.morada}, ${data.localidade}\nTipo: ${TIPO_LABEL[data.tipoImovel]}\nData preferida: ${data.dataVisita.split('-').reverse().join('/')}\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\nQualquer dúvida contacte-nos:\n📞 217 967 663 | ✉️ at@contera.pt\n\nAT CONTERA | Distribuidor Oficial Comelit`,
          }),
        });
      }
    } catch {
      // silencioso — lead guardado localmente
    }

    setLeadCode(code);
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/80 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">C</span>
            </div>
            <div>
              <span className="font-bold text-foreground text-base tracking-tight">AT CONTERA</span>
              <span className="text-muted-foreground text-xs block leading-none">66 anos · Videoporteiros</span>
            </div>
          </div>
          <nav className="flex items-center gap-1 sm:gap-2">
            <a href="tel:217967663" className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-primary hover:text-primary/80 transition-colors">
              <Phone size={14} />
              217 967 663
            </a>
            <a href="mailto:at@contera.pt" className="hidden sm:flex items-center gap-1.5 text-sm font-bold text-primary hover:text-primary/80 transition-colors mr-2">
              <Mail size={14} />
              at@contera.pt
            </a>
            <Button asChild variant="ghost" size="sm" className="text-xs sm:text-sm">
              <Link to={ROUTE_PATHS.GARANTIA}>Área do Cliente</Link>
            </Button>
            <Button asChild variant="ghost" size="sm" className="text-xs sm:text-sm">
              <Link to={ROUTE_PATHS.INSTALADOR}>Sou Instalador</Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="text-xs sm:text-sm">
              <Link to={ROUTE_PATHS.DASHBOARD}>Dashboard</Link>
            </Button>
          </nav>
        </div>
      </header>

      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 35 }}
            className="max-w-lg mx-auto px-4 py-20 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Pedido Recebido!</h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              O seu pedido foi registado com sucesso. A equipa entrará em contacto em breve através dos Instaladores Certificados Comelit da sua zona.
            </p>

            {/* Código CTR em destaque */}
            <div className="bg-primary rounded-2xl p-6 mb-5 text-center shadow-lg">
              <p className="text-primary-foreground/80 text-xs font-semibold uppercase tracking-wider mb-2">O seu código de cliente</p>
              <p className="font-mono font-extrabold text-3xl text-white tracking-widest mb-2">{leadCode}</p>
              <p className="text-primary-foreground/70 text-xs">Guarde este código — é o seu acesso a tudo.</p>
            </div>

            {/* CTA Área do Cliente */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-6 text-left">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0 mt-0.5">
                  <FileText size={18} className="text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-blue-900 text-sm mb-1">Acompanhe o estado do seu orçamento</p>
                  <p className="text-blue-700 text-xs leading-relaxed mb-3">
                    Assim que o instalador preparar uma proposta para a sua obra, pode consultá-la e aprová-la diretamente na <strong>Área do Cliente Final</strong> com o seu código <span className="font-mono font-bold">{leadCode}</span>.
                  </p>
                  <Link to={ROUTE_PATHS.GARANTIA}
                    className="inline-flex items-center gap-2 bg-blue-600 text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                    <Shield size={13} />
                    Ir para a Área do Cliente Final
                  </Link>
                </div>
              </div>
            </div>
            {/* Mini steps no sucesso */}
            <div className="text-left bg-card border border-border rounded-xl p-4 mb-6 space-y-3">
              {steps.map((s) => (
                <div key={s.num} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{s.num.replace('0','')}</span>
                  <div>
                    <p className="text-sm font-medium text-foreground">{s.title}</p>
                    <p className="text-xs text-muted-foreground">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mb-6">
               🛡️ Garantia 3 anos · Instaladores Certificados Comelit · 30%+70% seguro pela AT CONTERA
            </p>
            <Button variant="outline" onClick={() => setSubmitted(false)}>Fazer outro pedido</Button>
          </motion.div>
        ) : (
          <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-5xl mx-auto px-4 py-10">

            {/* Hero com slideshow */}
            <div className="relative w-full h-[420px] sm:h-[520px] rounded-2xl overflow-hidden mb-12 shadow-2xl">
              {/* Imagem de fundo com transição */}
              <div
                className="absolute inset-0 bg-cover bg-center transition-all duration-700"
                style={{
                  backgroundImage: `url(${slide.img})`,
                  opacity: fadeIn ? 1 : 0,
                }}
              />
              {/* Overlay escuro gradiente */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />

              {/* Conteúdo */}
              <div
                className="relative h-full flex flex-col items-center justify-center text-center px-6"
                style={{ opacity: fadeIn ? 1 : 0, transition: 'opacity 0.5s ease' }}
              >
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
                  <Shield size={12} />
                  Distribuidor Oficial Comelit · Portugal
                </div>
                <h1 className="text-3xl sm:text-5xl font-extrabold text-white mb-4 leading-tight drop-shadow-lg max-w-3xl">
                  {slide.frase}
                </h1>
                <p className="text-white/80 text-base sm:text-lg max-w-xl leading-relaxed mb-8">
                  {slide.sub}
                </p>

              </div>

              {/* Número de telefone no slide */}
              <div className="absolute top-4 right-4">
                <div className="flex flex-col gap-1.5">
                  <a href="tel:217967663" className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 text-white font-bold text-sm px-4 py-2 rounded-xl hover:bg-white/25 transition-colors">
                    <Phone size={14} />
                    217 967 663
                  </a>
                  <a href="mailto:at@contera.pt" className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 text-white font-semibold text-sm px-4 py-2 rounded-xl hover:bg-white/25 transition-colors">
                    <Mail size={14} />
                    at@contera.pt
                  </a>
                </div>
              </div>

              {/* Indicadores de slide */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setSlideIdx(i); setFadeIn(true); }}
                    className={`w-2 h-2 rounded-full transition-all ${i === slideIdx ? 'bg-white w-6' : 'bg-white/40'}`}
                  />
                ))}
              </div>
            </div>

            {/* Trust bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-12">
              {trustItems.map((f) => (
                <div key={f.title} className="bg-card border border-border rounded-xl p-4 flex gap-3 items-start">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <f.icon size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-xs text-foreground">{f.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Como Funciona */}
            <div className="mb-12">
              <h2 className="text-xl font-bold text-foreground text-center mb-2">Como Funciona</h2>
              <p className="text-muted-foreground text-sm text-center mb-8">Um processo simples, transparente e com total supervisão.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {steps.map((s, i) => (
                  <div key={s.num} className="relative bg-card border border-border rounded-2xl p-5 flex flex-col gap-3">
                    {i < steps.length - 1 && (
                      <ChevronRight size={16} className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
                    )}
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <s.icon size={20} className="text-primary" />
                      </div>
                      <span className="font-mono text-2xl font-bold text-primary/30">{s.num}</span>
                    </div>
                    <p className="font-semibold text-sm text-foreground">{s.title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Proposta de Orçamento */}
            <div className="mb-12 bg-gradient-to-br from-slate-50 to-blue-50 border border-blue-100 rounded-3xl p-6 sm:p-10">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                {/* Texto */}
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                    <FileText size={13} />
                    Proposta Certificada
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-3">
                    Receberá um orçamento<br />
                    <span className="text-primary">oficial e transparente</span>
                  </h2>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-5">
                    Os instaladores certificados Comelit emitem uma proposta formal com todos os dados, referência da obra, materiais e condições de garantia. Sem surpresas, sem letras pequenas.
                  </p>
                  <div className="space-y-2.5">
                    {[
                      { icon: '📋', text: 'Referência única da obra (ex: CTR-XXXXXXXX)' },
                      { icon: '🔐', text: 'NIF e dados do instalador certificado' },
                      { icon: '🛡️', text: 'Garantia de 3 anos incluída em todos os trabalhos' },
                      { icon: '💳', text: 'Pagamento gerido pela AT CONTERA: 30% sinal + 70% na conclusão da obra' },
                    ].map((item) => (
                      <div key={item.text} className="flex items-start gap-2.5">
                        <span className="text-base mt-0.5">{item.icon}</span>
                        <span className="text-sm text-foreground">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Imagem */}
                <div className="flex-shrink-0 w-full lg:w-auto flex flex-col items-center gap-3">
                  <div className="relative shadow-2xl rounded-xl overflow-hidden border border-slate-200 w-full max-w-xs lg:w-64">
                    <img
                      src="/proposta_preview.png"
                      alt="Exemplo de proposta de orçamento AT CONTERA"
                      className="w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                  </div>
                  <p className="text-xs text-muted-foreground text-center max-w-xs">
                    Exemplo de proposta emitida por um Instalador Certificado Comelit
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div id="pedido" className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
              <h2 className="text-xl font-bold text-foreground mb-2 flex items-center gap-2">
                <ChevronRight size={20} className="text-primary" />
                Pedido de Instalação
              </h2>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Contacto */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Phone size={14} />Dados de Contacto
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="nome">Nome completo *</Label>
                      <Input id="nome" placeholder="Ana Silva" {...register('nome')} />
                      {errors.nome && <p className="text-destructive text-xs">{errors.nome.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="telefone">Telefone *</Label>
                      <Input id="telefone" placeholder="912 345 678" {...register('telefone')} />
                      {errors.telefone && <p className="text-destructive text-xs">{errors.telefone.message}</p>}
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" placeholder="ana@email.com" {...register('email')} />
                      {errors.email && <p className="text-destructive text-xs">{errors.email.message}</p>}
                    </div>
                  </div>
                </div>

                {/* Tipo */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <HomeIcon size={14} />Tipo de Imóvel *
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {tipoOptions.map((opt) => {
                      const isActive = tipoSelecionado === opt.value;
                      return (
                        <button key={opt.value} type="button" onClick={() => setValue('tipoImovel', opt.value)}
                          className={`rounded-xl border-2 p-4 flex flex-col items-center gap-2 transition-all duration-200 cursor-pointer ${isActive ? 'border-primary bg-primary/8 text-primary' : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:bg-accent'}`}>
                          <opt.icon size={24} />
                          <span className="font-semibold text-sm">{opt.label}</span>
                          <span className="text-xs text-center leading-tight opacity-70">{opt.desc}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Localização */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <MapPin size={14} />Localização
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="morada">Morada *</Label>
                      <Input id="morada" placeholder="Rua das Flores, 12, 2º Dto" {...register('morada')} />
                      {errors.morada && <p className="text-destructive text-xs">{errors.morada.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="localidade">Localidade *</Label>
                      <Input id="localidade" placeholder="Lisboa" {...register('localidade')} />
                      {errors.localidade && <p className="text-destructive text-xs">{errors.localidade.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="codigoPostal">Código Postal *</Label>
                      <Input id="codigoPostal" placeholder="1000-001" {...register('codigoPostal')} />
                      {errors.codigoPostal && <p className="text-destructive text-xs">{errors.codigoPostal.message}</p>}
                    </div>
                  </div>
                </div>

                {/* Mapa dinâmico */}
                {mapsEmbed && (
                  <div className="rounded-xl overflow-hidden border border-border h-48">
                    <iframe
                      key={mapsEmbed}
                      src={mapsEmbed}
                      width="100%" height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Localização da obra"
                    />
                  </div>
                )}

                {/* Visita */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Calendar size={14} />Visita Técnica
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="dataVisita">Data preferida *</Label>
                      <Input id="dataVisita" type="date" min={minDate} {...register('dataVisita')} />
                      {errors.dataVisita && <p className="text-destructive text-xs">{errors.dataVisita.message}</p>}
                    </div>
                  </div>
                </div>

                {/* Notas */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                    <MessageSquare size={14} />Informações Adicionais
                  </h3>
                  <Textarea id="notas" placeholder="Ex: portão à direita, interfone avariado, horário preferido..." className="resize-none h-24" {...register('notas')} />
                </div>

                <Button type="submit" disabled={isSubmitting} size="lg" className="w-full text-base font-semibold h-12">
                  {isSubmitting ? 'A enviar...' : 'Solicitar Orçamento Gratuito'}
                  <ChevronRight size={18} className="ml-1" />
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Ao submeter, será contactado por Instaladores Certificados Comelit da sua zona. Garantia de 3 anos incluída em todos os trabalhos.
                </p>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="border-t border-border mt-16 py-8 text-center text-xs text-muted-foreground">
        <p>© {new Date().getFullYear()} AT CONTERA · 66 anos de excelência em videoporteiros</p>
        <p className="mt-1">Garantia de 3 anos · Instaladores Certificados Comelit · Pagamentos seguros</p>
      </footer>
    </div>
  );
}
